import { resolveIconHref } from "../resolve-icon.js";
import { cn, tv } from "tailwind-variants";

export type IconSizeScale = -4 | -3 | -2 | -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type IconIntentColor =
	| "primary"
	| "secondary"
	| "light"
	| "dark"
	| "white"
	| "success"
	| "info"
	| "warning"
	| "danger"
	| "none";

export type IconProps = {
	icon?: string;
	iconName?: string;
	caption?: string;
	size?: IconSizeScale;
	intentColor?: IconIntentColor;
	href?: string;
	trackingEvent?: string;
	noFollow?: boolean;
	target?: "_self" | "_blank" | "_parent" | "_top";
	ariaLabel?: string;
	className?: string;
};

const iconVariants = tv({
	base: "icon",
	variants: {
		size: {
			[-4]: "icon-12",
			[-3]: "icon-14",
			[-2]: "icon-16",
			[-1]: "icon-18",
			0: "icon-24",
			1: "icon-36",
			2: "icon-48",
			3: "icon-64",
			4: "icon-128",
			5: "icon-256",
			6: "icon-512",
		},
		intentColor: {
			primary: "text-primary",
			secondary: "text-secondary",
			light: "text-light",
			dark: "text-dark",
			white: "text-white",
			success: "text-success",
			info: "text-info",
			warning: "text-warning",
			danger: "text-danger",
			none: "",
		},
	},
});

export function Icon({
	icon,
	iconName,
	caption,
	size = 0,
	intentColor = "none",
	href,
	trackingEvent,
	noFollow,
	target,
	ariaLabel,
	className,
}: IconProps) {
	const ariaHidden = !caption;

	if (!icon) {
		return null;
	}

	const iconMarkup = (
		// biome-ignore lint/a11y/noSvgWithoutTitle: Matches the Razor block, which only renders a title when the icon has a caption.
		<svg
			className={cn(iconVariants({ size, intentColor }), !href && className)}
			aria-hidden={ariaHidden}
		>
			{caption ? <title>{caption}</title> : null}
			<use
				href={resolveIconHref(icon)}
			/>
		</svg>
	);

	if (href) {
		const accessibleLabel = ariaLabel ?? caption ?? iconName ?? icon;
		return (
			<a
				href={href}
				target={target}
				data-tracking-event={trackingEvent}
				rel={
					[
						target === "_blank" ? "noopener noreferrer" : "",
						noFollow ? "nofollow" : "",
					]
						.filter(Boolean)
						.join(" ") || undefined
				}
				aria-label={accessibleLabel}
				className={className}
			>
				{iconMarkup}
			</a>
		);
	}

	return iconMarkup;
}
